import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Tabs,
  Tab,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import CustomInput from "../../components/common/CustomInput";
import CustomTextArea from "../../components/common/CustomTextArea";
import CustomImageUploader from "../../components/common/CustomImageUploader";
import axios from "axios";
import config from "../../config";

const initialFormData = {
  title: "",
  description: "",
  coverImage: null,
  albumImages: [],
};

const UpdateAlbumModal = ({ open, onClose, onSave, albumId, albumSlug }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const modalContentRef = useRef(null);
  const [activeTab, setActiveTab] = useState(0);
  const [fetchingData, setFetchingData] = useState(true);
  const [albumData, setAlbumData] = useState(null);
  const [albumImages, setAlbumImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);

  // Fetch album data when modal opens
  useEffect(() => {
    if (open && albumSlug) {
      console.log("Modal mở, đang tải dữ liệu cho album slug:", albumSlug);
      fetchAlbumData();
    }
  }, [open, albumSlug]);

  const fetchAlbumData = async () => {
    setFetchingData(true);
    try {
      console.log("Đang gọi API với slug:", albumSlug);
      
      // Gọi API lấy thông tin album theo slug
      const response = await axios.get(`${config.apiBaseUrl}/Album/by-slug/${albumSlug}`);
      console.log("Kết quả API album:", response.data);
      
      const albumData = response.data;
      setAlbumData(albumData);
      
      // Lấy danh sách ảnh từ API (kiểu mới: albumData.images là mảng object {albumImageId, imageUrl})
      const albumImagesArr = (albumData.images || []).map(img => ({
        albumImageId: img.albumImageId,
        imageUrl: img.imageUrl,
        preview: img.imageUrl
      }));
      // Lấy ảnh bìa từ trường imageUrlsalbum
      const coverImageUrl = albumData.imageUrlsalbum?.[0] || null;
      const coverImageObj = coverImageUrl ? {
        url: coverImageUrl,
        preview: coverImageUrl
      } : null;
      // Cập nhật formData với cả thông tin album và danh sách ảnh
      setFormData({
        title: albumData.title || "",
        description: albumData.description || "",
        coverImage: coverImageObj,
        albumImages: albumImagesArr,
        newAlbumImages: []
      });
      
    } catch (error) {
      console.error("❌ Lỗi khi tải dữ liệu album:", error);
      alert("❌ Không thể tải dữ liệu album. Vui lòng thử lại sau.");
    } finally {
      setFetchingData(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCoverImageUpload = (newImages) => {
    // Chỉ lấy ảnh cuối cùng làm ảnh bìa
    setFormData((prev) => ({ 
      ...prev, 
      coverImage: newImages.length > 0 ? newImages[newImages.length - 1] : null 
    }));
  };

  const handleAlbumImagesUpload = (newImages) => {
    // Lưu vị trí scroll hiện tại trước khi cập nhật state
    const scrollPosition = modalContentRef.current?.scrollTop || 0;
    
    // Cập nhật state với tất cả ảnh
    setFormData((prev) => ({ ...prev, albumImages: newImages }));
    
    // Đảm bảo scroll position được khôi phục sau khi re-render
    requestAnimationFrame(() => {
      if (modalContentRef.current) {
        modalContentRef.current.scrollTop = scrollPosition;
      }
    });
  };

  const handleUpdate = async () => {
    if (!formData.title.trim()) {
      alert("❌ Tiêu đề album là bắt buộc.");
      return;
    }

    if (!formData.coverImage) {
      alert("❌ Vui lòng chọn ảnh bìa cho album.");
      return;
    }

    setLoading(true);
    try {
      // Bước 1: Cập nhật thông tin cơ bản của album
      const formDataToSend = new FormData();
      
      // Sử dụng albumId từ props nếu có, nếu không thì dùng từ albumData
      const albumIdToUse = albumId || (albumData && albumData.albumId);
      
      if (!albumIdToUse) {
        console.error("Không tìm thấy albumId trong props hoặc albumData:", { propsAlbumId: albumId, albumData });
        alert("❌ Không thể cập nhật album: Thiếu thông tin albumId");
        setLoading(false);
        return;
      }
      
      console.log("albumId trước khi gửi:", albumIdToUse, "kiểu:", typeof albumIdToUse);
      
      // Đảm bảo albumId là chuỗi khi gửi đi
      formDataToSend.append("albumId", albumIdToUse.toString());
      formDataToSend.append("title", formData.title.trim());
      formDataToSend.append("description", formData.description || "");
      
      // Thêm ảnh bìa mới nếu có
      if (formData.coverImage && formData.coverImage.file) {
        formDataToSend.append("coverImage", formData.coverImage.file);
      }
      
      // Thêm ảnh bìa URL nếu có
      if (formData.coverImage && formData.coverImage.url) {
        formDataToSend.append("coverImageUrl", formData.coverImage.url);
      }

      // Log chi tiết dữ liệu gửi đi
      console.log("Đang gửi request update album với ID:", albumIdToUse);
      console.log("URL API đang gọi:", `${config.apiBaseUrl}/Album/update/${albumIdToUse}`);

      // In ra tất cả các cặp key-value trong FormData để kiểm tra
      for (let pair of formDataToSend.entries()) {
        console.log(pair[0] + ': ' + pair[1]);
      }

      // Gọi API với albumId trong URL
      const response = await axios.put(
        `${config.apiBaseUrl}/Album/update/${albumIdToUse}`, 
        formDataToSend, 
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      console.log("Kết quả API update album:", response);
      console.log("Status:", response.status);
      console.log("Data:", response.data);

      if (response.status === 200) {
        // Bước 2: Cập nhật ảnh trong album (nếu có ảnh mới)
        const newImages = formData.albumImages.filter(img => img.file);
        const albumImagesFormData = new FormData();
        albumImagesFormData.append("albumId", albumIdToUse.toString());
        newImages.forEach((image, index) => {
          if (image.file) {
            albumImagesFormData.append("images", image.file);
          }
        });
        try {
          const addImagesResponse = await axios.post(
            `${config.apiBaseUrl}/Album/add-images`,
            albumImagesFormData,
            { headers: { "Content-Type": "multipart/form-data" } }
          );
          if (addImagesResponse.status !== 200) {
            alert("⚠️ Album đã được cập nhật nhưng có lỗi khi thêm ảnh mới.");
          }
        } catch (error) {
          const errorMessage = error.response?.data?.Message || 
                              error.response?.data?.message || 
                              error.response?.data?.error ||
                              JSON.stringify(error.response?.data) ||
                              error.message ||
                              "Không xác định";
          alert(`⚠️ Album đã được cập nhật nhưng có lỗi khi thêm ảnh mới: ${errorMessage}`);
        }
        alert(`✅ Album đã được cập nhật thành công!`);
        onSave?.();
        onClose();
      } else {
        alert("❌ Có lỗi xảy ra khi cập nhật album.");
      }
    } catch (error) {
      console.error("❌ Lỗi khi gửi yêu cầu:", error);
      console.error("Chi tiết lỗi:", {
        message: error.message,
        response: error.response,
        request: error.request
      });
      
      if (error.response) {
        console.error("Response status:", error.response.status);
        console.error("Response data:", error.response.data);
        
        // Hiển thị thông tin lỗi chi tiết hơn
        const errorMessage = error.response.data?.Message || 
                             error.response.data?.message || 
                             error.response.data?.error ||
                             JSON.stringify(error.response.data) ||
                             "Không xác định";
        
        alert(`❌ Lỗi API: ${error.response.status} - ${errorMessage}`);
      } else if (error.request) {
        console.error("Không nhận được phản hồi từ server");
        alert("❌ Không thể kết nối đến server!");
      } else {
        console.error("Lỗi cấu hình request");
        alert("❌ Lỗi khi thiết lập yêu cầu!");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData(initialFormData);
    onClose();
  };

  // Hàm tải danh sách ảnh trong album
  const loadAlbumImages = async () => {
    if (!albumData || !albumData.albumId) {
      console.warn("Không có albumId, không thể tải ảnh");
      return;
    }
    
    setLoadingImages(true);
    try {
      console.log("Đang tải danh sách ảnh cho album ID:", albumData.albumId);
      
      // Sử dụng API album/all để lấy danh sách album
      const response = await axios.get(`${config.apiBaseUrl}/Album/all`);
      console.log("API Album/all response:", response.data);
      
      // Tìm album hiện tại trong danh sách
      const currentAlbum = response.data.find(album => album.albumId === albumData.albumId);
      console.log("Album tìm thấy:", currentAlbum);
      
      if (currentAlbum && currentAlbum.imageUrls) {
        // Lấy danh sách ảnh từ album
        const imageUrls = currentAlbum.imageUrls || [];
        console.log("Danh sách ảnh từ album:", imageUrls);
        
        // Chuyển đổi danh sách URL thành định dạng phù hợp với CustomImageUploader
        const formattedImages = imageUrls.map(url => ({
          url: url,
          preview: url
        }));
        console.log("Danh sách ảnh đã định dạng:", formattedImages);
        
        // Cập nhật state
        setAlbumImages(formattedImages);
        setFormData(prev => ({
          ...prev,
          albumImages: formattedImages
        }));
      } else {
        console.warn("Không tìm thấy album hoặc album không có ảnh");
        setAlbumImages([]);
        setFormData(prev => ({
          ...prev,
          albumImages: []
        }));
      }
    } catch (error) {
      console.error("❌ Lỗi khi tải danh sách ảnh album:", error);
      alert("❌ Không thể tải danh sách ảnh album. Vui lòng thử lại sau.");
    } finally {
      setLoadingImages(false);
    }
  };

  // Xử lý khi chuyển tab
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    
    // Nếu chuyển sang tab "Thêm Ảnh" (tab 1), luôn tải lại danh sách ảnh
    if (newValue === 1 && albumData) {
      loadAlbumImages();
    }
  };

  // Hàm xóa ảnh khỏi album (call API xóa theo albumImageId hoặc url)
  const handleDeleteImage = async (imgObj) => {
    if (!albumId && !(albumData && albumData.albumId)) {
      alert("Không tìm thấy albumId để xóa ảnh!");
      return;
    }
    const albumIdToUse = albumId || (albumData && albumData.albumId);
    const albumImageId = imgObj.albumImageId;
    if (albumImageId) {
      if (!window.confirm("Bạn có chắc chắn muốn xóa ảnh này khỏi album?")) return;
      try {
        const formData = new FormData();
        formData.append("albumId", albumIdToUse);
        formData.append("albumImageIds", albumImageId);
        const response = await axios.post(
          `${config.apiBaseUrl}/Album/DeleteImages`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        if (response.status === 200) {
          setFormData(prev => ({
            ...prev,
            albumImages: prev.albumImages.filter(img => img.albumImageId !== albumImageId)
          }));
          alert("Đã xóa ảnh khỏi album!");
        } else {
          alert("Có lỗi khi xóa ảnh khỏi album!");
        }
      } catch (error) {
        alert("Lỗi khi xóa ảnh khỏi album: " + (error.response?.data?.Message || error.message));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        albumImages: prev.albumImages.filter(img => img !== imgObj)
      }));
    }
  };

  return (
    <Modal open={open} onClose={handleClose} keepMounted>
      <Box sx={{ 
        position: "absolute", 
        top: "50%", 
        left: "50%", 
        transform: "translate(-50%, -50%)", 
        width: "90%", 
        maxWidth: "900px", 
        bgcolor: "background.paper", 
        boxShadow: 10, 
        p: 3, 
        borderRadius: 2, 
        display: "flex", 
        flexDirection: "column", 
        maxHeight: "85vh", 
        overflow: "hidden" 
      }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold", textAlign: "center" }}>
          Chỉnh Sửa Album
        </Typography>
        
        {fetchingData ? (
          <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange} 
              centered
              sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Thông tin Album" />
              <Tab label="Thêm Ảnh" />
            </Tabs>
            
            <Box 
              ref={modalContentRef}
              sx={{ 
                flexGrow: 1, 
                overflowY: "auto", 
                p: 2,
                scrollBehavior: "smooth",
                "&::-webkit-scrollbar": {
                  width: "8px"
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(0,0,0,0.2)",
                  borderRadius: "4px"
                }
              }}
            >
              {activeTab === 0 && (
                <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2, bgcolor: "#f9f9f9" }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: "#1976d2" }}>
                    Thông tin Album {formData.title ? `- ${formData.title}` : ""}
                  </Typography>
                  
                  <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ mb: 4 }}>
                        <CustomInput 
                          label="Tiêu đề" 
                          name="title" 
                          value={formData.title} 
                          onChange={handleInputChange} 
                          required 
                          fullWidth
                        />
                      </Box>
                      
                      <Box>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                          Mô tả chi tiết
                        </Typography>
                        <CustomTextArea 
                          initialContent={formData.description} 
                          onChange={(val) => setFormData(prev => ({ ...prev, description: val }))} 
                          style={{ 
                            minHeight: "150px",
                            height: "150px",
                            width: "100%",
                            maxWidth: "100%"
                          }}
                        />
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Box>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                          Ảnh bìa album
                        </Typography>
                        <CustomImageUploader 
                          onUpload={handleCoverImageUpload} 
                          value={formData.coverImage ? [formData.coverImage] : []} 
                          onChange={handleCoverImageUpload} 
                          style={{
                            minHeight: "200px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center"
                          }}
                          maxImages={1}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                          Ảnh bìa sẽ hiển thị ở trang chính của album
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              )}
              
              {activeTab === 1 && (
                <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2, bgcolor: "#f9f9f9" }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: "#1976d2" }}>
                    Ảnh Trong Album
                  </Typography>
                  
                  {loadingImages ? (
                    <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                      <CircularProgress size={30} />
                      <Typography variant="body2" sx={{ ml: 2 }}>
                        Đang tải danh sách ảnh...
                      </Typography>
                    </Box>
                  ) : (
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        Hình ảnh hiện có trong album ({formData.albumImages?.length || 0} ảnh)
                      </Typography>
                      <CustomImageUploader 
                        onUpload={(newImages) => {
                          setFormData(prev => ({...prev, albumImages: newImages}));
                        }} 
                        value={formData.albumImages} 
                        onChange={(newImages) => {
                          setFormData(prev => ({...prev, albumImages: newImages}));
                        }} 
                        onDeleteImage={async (imgObj) => {
                          await handleDeleteImage(imgObj); // chỉ cập nhật state khi API thành công (đã có trong handleDeleteImage)
                        }}
                        style={{
                          minHeight: "300px",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center"
                        }}
                      />
                      {(!formData.albumImages || formData.albumImages.length === 0) && (
                        <Typography variant="body2" color="text.secondary" sx={{ my: 2, textAlign: 'center' }}>
                          Album này chưa có ảnh nào. Hãy thêm ảnh mới cho album!
                        </Typography>
                      )}
                    </Box>
                  )}
                </Paper>
              )}
            </Box>
            
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2, p: 1, borderTop: "1px solid #ddd", gap: 2 }}>
              {activeTab === 0 ? (
                <>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    sx={{ 
                      width: "40%", 
                      fontWeight: "bold",
                      textTransform: "uppercase"
                    }} 
                    onClick={() => setActiveTab(1)}
                  >
                    Tiếp Theo
                  </Button>
                  <Button 
                    variant="outlined" 
                    color="error" 
                    sx={{ 
                      width: "40%", 
                      fontWeight: "bold",
                      textTransform: "uppercase"
                    }} 
                    onClick={handleClose}
                  >
                    Hủy
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    sx={{ 
                      width: "40%", 
                      fontWeight: "bold",
                      textTransform: "uppercase"
                    }} 
                    onClick={handleUpdate} 
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : "Cập Nhật Album"}
                  </Button>
                  <Button 
                    variant="outlined" 
                    color="primary" 
                    sx={{ 
                      width: "40%", 
                      fontWeight: "bold",
                      textTransform: "uppercase"
                    }} 
                    onClick={() => setActiveTab(0)}
                  >
                    Quay Lại
                  </Button>
                </>
              )}
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default UpdateAlbumModal;












































